import React, { useState, useRef } from "react";
import CloseIcon from "@mui/icons-material/Close";
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import { CreateTableNew as CreateTable } from "../../../../../common/APICommon";

const UploadCvtLogsPreviewModal = ({ isOpen, onClose, onSubmit, previewData, onFileSelect, loading }) => {
  const [currentPage, setCurrentPage] = useState(1);
  const [isDragging, setIsDragging] = useState(false);
  const fileInputRef = useRef(null);

  if (!isOpen) return null;

  const handleDragOver = (e) => { e.preventDefault(); setIsDragging(true); };
  const handleDragLeave = () => setIsDragging(false);
  const handleDrop = (e) => {
    e.preventDefault();
    setIsDragging(false);
    const file = e.dataTransfer.files[0];
    if (file && (file.name.endsWith('.csv') || file.name.endsWith('.xlsx'))) onFileSelect(file);
  };

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (file) onFileSelect(file);
  };

  const customRender = (row, accessor) => {
    if (accessor === "dateTimeLog")
      return row[accessor] ? new Date(row[accessor]).toLocaleString() : "-";
    if (accessor === "recordType") {
      const type = row[accessor]?.toUpperCase();
      return (
        <span className={`font-bold ${type === "IN" ? "text-green-600" : "text-orange-600"}`}>
          {type || "-"}
        </span>
      );
    }
    if (accessor === "errors" && row[accessor])
      return <span className="text-red-600 font-bold">{row[accessor]}</span>;
    return row[accessor] || "-";
  };

  // MODIFIED: Calculate valid count for the button label
  const validCount = previewData.filter(i => !i.errors).length;

  return (
    <div className="fixed inset-0 z-[10000] bg-black/50 flex items-center justify-center p-3 overflow-auto">
      <div className="bg-white w-full md:w-[90%] lg:w-[75%] rounded-lg shadow-2xl flex flex-col max-h-[90vh]">
        <div className="flex justify-between items-center px-6 py-4 border-b border-gray-200 bg-gray-50 rounded-t-lg">
          <div className="flex flex-col">
            <h2 className="text-xl font-bold text-blue-900 tracking-tight">CVT File Upload Preview</h2>
            <p className="text-xs text-gray-500 uppercase font-semibold">Verify Device-to-Employee Mapping</p>
          </div>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-700 bg-transparent border-none cursor-pointer">
            <CloseIcon fontSize="medium" />
          </button>
        </div>

        <div className="p-6 overflow-y-auto flex-grow">
          <div className="mb-6 flex flex-col gap-2">
            <span className="text-sm font-bold text-blue-800 uppercase">Step 1: Select Biometrics CSV File</span>
            <div
              onDragOver={handleDragOver} onDragLeave={handleDragLeave} onDrop={handleDrop}
              onClick={() => fileInputRef.current.click()}
              className={`relative border-2 border-dashed rounded-xl p-8 flex flex-col items-center justify-center transition-all cursor-pointer
                ${isDragging ? "border-blue-500 bg-blue-100" : "border-blue-200 bg-blue-50 hover:bg-blue-100/50"}
                ${loading ? "opacity-50 pointer-events-none" : ""}`}
            >
              <input type="file" ref={fileInputRef} accept=".csv" onChange={handleFileChange} className="hidden" />
              <CloudUploadIcon className={`${isDragging ? "text-blue-600" : "text-blue-400"} mb-2`} sx={{ fontSize: 48 }} />
              <div className="text-center">
                <p className="text-sm font-bold text-blue-900">{loading ? "Processing..." : "Click or Drag CSV file here"}</p>
                <p className="text-xs text-blue-500 font-medium mt-1">Mapping logic will use "Device Number" from Masterlist</p>
              </div>
            </div>
          </div>

          <div className="mb-2 flex items-center justify-between">
            <span className="text-sm font-bold text-gray-600 uppercase px-1">Step 2: Preview Mapping Results</span>
            {previewData.length > 0 && <span className="text-xs font-bold text-blue-700 bg-blue-100 px-2 py-1 rounded">{previewData.length} Records Detected</span>}
          </div>

          <div className="border rounded-lg overflow-hidden shadow-sm">
            <CreateTable
              tableName="CvtUploadPreview"
              headers={["Mapped ID", "Employee Name", "Date Time", "Log Type", "Status"]}
              accessors={["employeeId", "fullName", "dateTimeLog", "recordType", "errors"]}
              data={previewData}
              currentPage={currentPage}
              setCurrentPage={setCurrentPage}
              itemsPerPage={5}
              totalItems={previewData.length}
              customCellRenderer={customRender}
            />
          </div>
        </div>

        <div className="flex justify-end gap-2 p-4 border-t bg-gray-50 rounded-b-lg">
          <button onClick={onClose} className="bg-gray-400 hover:bg-gray-500 text-white px-6 py-2 rounded-lg font-bold border-none cursor-pointer">Cancel</button>
          <button
            // MODIFIED: Button enabled if there is at least one valid record
            disabled={loading || previewData.length === 0 || validCount === 0}
            onClick={() => onSubmit(previewData)}
            className="bg-green-600 hover:bg-green-700 text-white px-10 py-2 rounded-lg font-bold border-none cursor-pointer shadow-md disabled:opacity-50"
          >
            {loading ? "Processing..." : `Sync ${validCount} Valid Logs`}
          </button>
        </div>
      </div>
    </div>
  );
};

export default UploadCvtLogsPreviewModal;